import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { useNavigate } from 'react-router-dom';

import { FocusGrid, HubContainerGridStyled, HubMainWrapperStyled } from '#client/components/Grid';
import { MODAL_NAMES } from '#client/constants/modals';
import useBreakpoints from '#client/hooks/useBreakpoints';

import {
  BankingFeatureWrapper,
  BoxStyled,
  ButtonStyled,
  ContentGridWrapperLeft,
  ContentGridWrapperRight,
  ContentLeftArea,
  ContentRightArea,
  DividerStyled,
  ImageLeftArea,
  ImageRightArea,
  SharedAssetStyled
} from './BankingFeature.styles';

export default function BankingFeature() {
  const navigate = useNavigate();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const trackButtonClick = useButtonPressedCallback({
    elementText: 'Start now',
    elementName: 'Banking_application_CTA',
    appPageName: 'banking_upsell_dashboard'
  });

  const handleClick = () => {
    /**
     * Question: How many users click the button?
     */
    trackButtonClick();
    navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`);
  };

  return (
    <HubContainerGridStyled>
      <FocusGrid>
        <HubMainWrapperStyled mt={{ xs: '48px', md: '96px' }}>
          <Heading
            size="2xl"
            weight="bold"
          >
            Banking Features
          </Heading>
          <DividerStyled />
        </HubMainWrapperStyled>
        <BankingFeatureWrapper>
          <Box mb={{ xs: '96px', md: '120px' }}>
            <Stack
              gap={{ xs: '64px', md: '80px' }}
              marginTop={{ xs: '24px', md: '80px' }}
            >
              <ContentGridWrapperLeft>
                <ContentLeftArea
                  gap={{ xs: '4px', md: '16px' }}
                  justifyContent="center"
                >
                  <BoxStyled mb={{ xs: '12px', md: 0 }}>
                    <Text
                      size="xs"
                      uppercase
                      weight="medium"
                    >
                      Get started
                    </Text>
                  </BoxStyled>
                  <Heading
                    size="2xl"
                    weight="bold"
                  >
                    Bank & run your business in one place
                  </Heading>
                  <Text size="lg">Trade all the apps for one simple solution.</Text>
                </ContentLeftArea>
                <ImageRightArea>
                  <SharedAssetStyled
                    alt=""
                    src="/s2/vector/bank-and-run-in-one-place.svg"
                  />
                </ImageRightArea>
              </ContentGridWrapperLeft>
              <ContentGridWrapperRight>
                <ImageLeftArea>
                  <SharedAssetStyled
                    alt=""
                    src="/s2/vector/apply-in-a-few-clicks.svg"
                  />
                </ImageLeftArea>
                <ContentRightArea
                  gap={{ xs: '4px', md: '16px' }}
                  justifyContent="center"
                >
                  <BoxStyled mb={{ xs: '12px', md: 0 }}>
                    <Text
                      size="xs"
                      uppercase
                      weight="medium"
                    >
                      Get started
                    </Text>
                  </BoxStyled>
                  <Heading
                    size="2xl"
                    weight="bold"
                  >
                    Apply in a few clicks
                  </Heading>
                  <Text size="lg">
                    Since we have your business info, applying is easy with a faster track to
                    approval.
                  </Text>
                </ContentRightArea>
              </ContentGridWrapperRight>
              <ContentGridWrapperLeft>
                <ContentLeftArea
                  gap={{ xs: '4px', md: '16px' }}
                  justifyContent="center"
                >
                  <BoxStyled>
                    <Text
                      size="xs"
                      uppercase
                      weight="medium"
                    >
                      money out
                    </Text>
                  </BoxStyled>
                  <Heading
                    size="2xl"
                    weight="bold"
                  >
                    2 types of debit cards
                  </Heading>
                  <Text size="lg">
                    Use your virtual or physical card as needed for more convenience and security.
                  </Text>
                </ContentLeftArea>
                <ImageRightArea>
                  <SharedAssetStyled
                    alt=""
                    src="/s2/vector/two-types-of-debit-cards.svg"
                  />
                </ImageRightArea>
              </ContentGridWrapperLeft>
              <ContentGridWrapperRight>
                <ImageLeftArea>
                  <SharedAssetStyled
                    alt=""
                    src="/s2/vector/many-ways-to-add-funds.svg"
                  />
                </ImageLeftArea>
                <ContentRightArea
                  gap={{ xs: '4px', md: '16px' }}
                  justifyContent="center"
                >
                  <BoxStyled>
                    <Text
                      size="xs"
                      uppercase
                      weight="medium"
                    >
                      money in
                    </Text>
                  </BoxStyled>
                  <Heading
                    size="2xl"
                    weight="bold"
                  >
                    Many ways to add funds
                  </Heading>
                  <Text size="lg">
                    Easily fund your business from a personal account and accept customer payments.
                  </Text>
                </ContentRightArea>
              </ContentGridWrapperRight>
              <ContentGridWrapperLeft>
                <ContentLeftArea
                  gap={{ xs: '4px', md: '16px' }}
                  justifyContent="center"
                >
                  <BoxStyled>
                    <Text
                      size="xs"
                      uppercase
                      weight="medium"
                    >
                      money in
                    </Text>
                  </BoxStyled>
                  <Heading
                    size="2xl"
                    weight="bold"
                  >
                    Deposit checks in a snap
                  </Heading>
                  <Text size="lg">
                    Use mobile check deposit to add funds and payments to your account, asap.
                  </Text>
                </ContentLeftArea>
                <ImageRightArea>
                  <SharedAssetStyled
                    alt=""
                    src="/s2/vector/deposit-checks-in-a-snap.svg"
                  />
                </ImageRightArea>
              </ContentGridWrapperLeft>
            </Stack>
            <Stack
              alignItems="center"
              mt="64px"
            >
              <Box>
                <SharedAsset
                  alt=""
                  height={isDesktop ? 120 : 80}
                  src="/s2/vector/product-zb-banking.svg"
                  width={isDesktop ? 120 : 80}
                />
              </Box>
              <Stack
                alignItems="center"
                gap={{ xs: '4px', md: '16px' }}
                mt={{ xs: '16px', md: '32px' }}
              >
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Bank like a boss
                </Heading>
                <Text size="xl">Applying is easy... just a few clicks</Text>
              </Stack>
              <ButtonStyled
                color="secondary"
                onClick={handleClick}
                size={isDesktop ? 'medium' : 'large'}
                variant="contained"
              >
                Start now
              </ButtonStyled>
            </Stack>
          </Box>
        </BankingFeatureWrapper>
      </FocusGrid>
    </HubContainerGridStyled>
  );
}
